/**
 * AbaNoteGenerationService — Frontend Proxy
 *
 * Calls the secure `generateAbaSessionNote` Firebase Cloud Function. The
 * function fetches the RBT draft + best-effort patient context from Firestore
 * server-side, calls Claude with the chosen ABA template, and persists the
 * resulting draft onto sessionNote.aiNoteDraft.
 *
 * Templates:
 *  - SOAP     — generic clinical Subjective/Objective/Assessment/Plan
 *  - ABC      — Antecedent-Behavior-Consequence functional analysis
 *  - DTT      — Discrete Trial Training (programs, trials, prompts, mastery)
 *  - NET      — Natural Environment Training narrative
 *  - Progress — goal-by-goal status (treatment plan reviews / authorizations)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

export type AbaTemplate = 'SOAP' | 'ABC' | 'DTT' | 'NET' | 'Progress';

export interface AbaNoteSuggestion {
  template: AbaTemplate;
  content: string;
  sections: Record<string, string>;
  confidence: number; // 0.0 - 1.0
  warnings: string[];
  sources: string[];
  modelUsed: string;
  generatedAt: string; // ISO string
}

interface GenerateAbaSessionNoteRequest {
  sessionNoteId: string;
  template: AbaTemplate;
  customInstructions?: string;
}

interface GenerateAbaSessionNoteResponse {
  success: boolean;
  data?: AbaNoteSuggestion;
  error?: string;
}

class AbaNoteGenerationService {
  /**
   * Generate an ABA-templated draft note for a session note that already has
   * an RBT draft submitted.
   */
  async generateNote(
    sessionNoteId: string,
    template: AbaTemplate,
    customInstructions?: string
  ): Promise<AbaNoteSuggestion> {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('You must be logged in to generate session notes');
    }

    const functions = getFunctions();
    const callable = httpsCallable<
      GenerateAbaSessionNoteRequest,
      GenerateAbaSessionNoteResponse
    >(functions, 'generateAbaSessionNote');

    try {
      const result = await callable({ sessionNoteId, template, customInstructions });
      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Note generation failed');
      }
      return result.data.data;
    } catch (error: any) {
      console.error('Error calling generateAbaSessionNote:', error);

      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to generate session notes');
      }
      if (error.code === 'permission-denied') {
        throw new Error('You do not have access to this session note');
      }
      if (error.code === 'not-found') {
        throw new Error('Session note not found');
      }
      if (error.code === 'failed-precondition') {
        throw new Error('Session note does not have an RBT draft yet');
      }
      if (error.code === 'invalid-argument') {
        throw new Error(
          error.message || 'sessionNoteId and a valid template are required'
        );
      }
      throw new Error(
        error.message || 'Failed to generate session note. Please try again later.'
      );
    }
  }
}

export const abaNoteGenerationService = new AbaNoteGenerationService();
